import { createAuthenticatedGraphQLClient } from '../../auth/createAuthenticatedGraphQLClient';
import { resolveClientEnv } from '../../graphql/config/resolveClientEnv';
import type {
  CanvassingSessionDoorVisitMapQuery,
  CanvassingSessionDoorVisitMapQueryVariables,
} from '../../graphql/queries/canvassingSessionDoorVisitMap.generated';
import type { VotersInBoundsQuery, VotersInBoundsQueryVariables } from '../../graphql/queries/votersInBounds.generated';
import { GraphQLRequestError } from '../../graphql/routing/apiRouting';
import {
  CANVASSING_SESSION_DOOR_VISIT_MAP_DOCUMENT,
  VOTERS_IN_BOUNDS_DOCUMENT,
} from '../canvassingSessionDocuments';
import type { CanvassingSessionFetchImpl } from './canvassingSessionApi';
import {
  MAP_OVERLAY_COPY,
  MapOverlayPageError,
  normalizeDoorVisitMapPage,
  normalizeVotersInBoundsPage,
  type MapOverlayBounds,
  type MapOverlayPage,
  type MapOverlayVisit,
  type MapOverlayVoter,
} from './canvassingSessionMapOverlay';

export const MAP_OVERLAY_PAGE_SIZE = 500;
export const VOTER_ADMIN_ACCESS_REQUIRED = 'Structurely admin access required';
export const INVALID_MAP_CURSOR_MESSAGE = 'Invalid or expired map cursor';

export type MapOverlayFailureKind = 'authorization' | 'expired-cursor' | 'request' | 'malformed';

export type MapOverlayFailure = {
  kind: MapOverlayFailureKind;
  message: string;
};

export { MapOverlayPageError } from './canvassingSessionMapOverlay';

type MapOverlayRequestOptions = {
  signal: AbortSignal;
  fetchImpl?: CanvassingSessionFetchImpl;
};

export type FetchVotersInBoundsInput = MapOverlayRequestOptions & {
  bounds: MapOverlayBounds;
  first: number;
  after?: string;
};

export type FetchDoorVisitMapInput = MapOverlayRequestOptions & {
  sessionId: string;
  first: number;
  after?: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

export const isAbortError = (error: unknown): boolean => isRecord(error) && Reflect.get(error, 'name') === 'AbortError';

const graphQLErrorEntries = (error: GraphQLRequestError): { message: string; code: string | null }[] =>
  (error.graphQLErrors ?? []).flatMap(entry => {
    if (!isRecord(entry)) return [];
    const message = Reflect.get(entry, 'message');
    const extensions = Reflect.get(entry, 'extensions');
    const code = isRecord(extensions) ? Reflect.get(extensions, 'code') : null;
    return [{
      message: typeof message === 'string' ? message.trim() : '',
      code: typeof code === 'string' ? code : null,
    }];
  });

export const isVoterAuthorizationFailure = (error: unknown): boolean => {
  if (error instanceof Error && error.message.trim() === VOTER_ADMIN_ACCESS_REQUIRED) return true;
  if (!(error instanceof GraphQLRequestError)) return false;
  if (error.status === 403) return true;
  return graphQLErrorEntries(error).some(entry =>
    entry.code === 'FORBIDDEN' || entry.message === VOTER_ADMIN_ACCESS_REQUIRED,
  );
};

export const isExpiredMapCursorError = (error: unknown): boolean => {
  if (error instanceof GraphQLRequestError) {
    return graphQLErrorEntries(error).some(entry => entry.message === INVALID_MAP_CURSOR_MESSAGE);
  }
  return error instanceof Error && error.message.trim() === INVALID_MAP_CURSOR_MESSAGE;
};

export const classifyMapOverlayError = (error: unknown, layer: 'voters' | 'visits'): MapOverlayFailure => {
  if (layer === 'voters' && isVoterAuthorizationFailure(error)) {
    return { kind: 'authorization', message: VOTER_ADMIN_ACCESS_REQUIRED };
  }
  if (isExpiredMapCursorError(error)) {
    return { kind: 'expired-cursor', message: INVALID_MAP_CURSOR_MESSAGE };
  }
  if (error instanceof MapOverlayPageError) {
    return { kind: 'malformed', message: MAP_OVERLAY_COPY.malformed };
  }
  return {
    kind: 'request',
    message: layer === 'voters' ? MAP_OVERLAY_COPY.voterRequestError : MAP_OVERLAY_COPY.visitRequestError,
  };
};

const request = async <TData>(
  document: Parameters<ReturnType<typeof createAuthenticatedGraphQLClient>['request']>[0]['document'],
  variables: VotersInBoundsQueryVariables | CanvassingSessionDoorVisitMapQueryVariables,
  operationName: string,
  options: MapOverlayRequestOptions,
): Promise<TData> => {
  const client = createAuthenticatedGraphQLClient({ env: resolveClientEnv(), fetchImpl: options.fetchImpl });
  return client.request<TData>({ document, variables, operationName, signal: options.signal });
};

export const fetchVotersInBounds = async ({
  bounds,
  first,
  after,
  signal,
  fetchImpl,
}: FetchVotersInBoundsInput): Promise<MapOverlayPage<MapOverlayVoter>> => {
  const variables: VotersInBoundsQueryVariables = {
    bounds: { north: bounds.north, south: bounds.south, east: bounds.east, west: bounds.west },
    first,
    ...(after === undefined ? {} : { after }),
  };
  const data = await request<VotersInBoundsQuery>(VOTERS_IN_BOUNDS_DOCUMENT, variables, 'VotersInBounds', { signal, fetchImpl });
  try {
    return normalizeVotersInBoundsPage(data.votersInBounds);
  } catch (error) {
    if (error instanceof MapOverlayPageError) throw error;
    throw new MapOverlayPageError();
  }
};

export const fetchCanvassingSessionDoorVisitMap = async ({
  sessionId,
  first,
  after,
  signal,
  fetchImpl,
}: FetchDoorVisitMapInput): Promise<MapOverlayPage<MapOverlayVisit>> => {
  const variables: CanvassingSessionDoorVisitMapQueryVariables = {
    sessionId,
    first,
    ...(after === undefined ? {} : { after }),
  };
  const data = await request<CanvassingSessionDoorVisitMapQuery>(
    CANVASSING_SESSION_DOOR_VISIT_MAP_DOCUMENT,
    variables,
    'CanvassingSessionDoorVisitMap',
    { signal, fetchImpl },
  );
  try {
    return normalizeDoorVisitMapPage(data.canvassingSessionDoorVisitMap);
  } catch (error) {
    if (error instanceof MapOverlayPageError) throw error;
    throw new MapOverlayPageError();
  }
};
